import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { TiTick } from 'react-icons/ti';
import { FaCheckCircle } from 'react-icons/fa';
import axios from 'axios';

import { formatRut } from '../../utils/rut';
import { headerRecaptcha } from '../../utils/recaptcha';

/**
 * Checkout exclusivo Caja Los Andes. Dos entradas posibles, ambas resuelven el RUT
 * server-side (nunca se teclea):
 *   - `?ref=...`  → afiliación validada en vivo (desde el landing /postular).
 *   - `?inv=...`  → invitación pre-aprobada por CLA (link enviado por correo);
 *                   además autocompleta nombre/apellido/teléfono (editables) y el
 *                   correo (read-only).
 * Al aprobarse el pago ($15.000), el backend matricula en el SIS y crea la cuenta
 * en empieza. Sin ref ni invitación válidos no se puede continuar.
 */

const PROGRAMA = 'Formación en IA y Productividad';
const COPAGO = '$15.000';
const REFERENCIA = '$560.000';

type FormValues = {
  name: string;
  lastname: string;
  email: string;
  country: string;
  phone?: string;
  consent: boolean;
};

const field =
  'w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-[#1A73E8] focus:ring-2 focus:ring-[#1A73E8]/20';
const label = 'mb-1 block text-sm font-semibold text-gray-700';

export const ClaCheckoutPage = () => {
  const [params] = useSearchParams();
  const ref = params.get('ref') || '';
  const inv = params.get('inv') || '';
  const isInvitation = !!inv;

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // El RUT nunca se teclea: viene validado — afiliación en vivo (`ref`, desde el
  // landing /postular) o PRE-APROBADA por CLA (invitación `inv`, link por correo).
  // Se resuelve server-side y acá solo se MUESTRA read-only. En modo invitación,
  // además autocompletamos nombre/apellido/teléfono (editables) y el correo (read-only).
  const [rut, setRut] = useState('');
  const [status, setStatus] = useState<'loading' | 'ok' | 'invalid'>('loading');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { country: 'Chile' } });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (isInvitation) {
          // Invitación pre-aprobada: trae RUT + datos para autocompletar.
          const { data } = await axios.get(
            `${process.env.REACT_APP_API_BACKEND}/cla/invitation`,
            { params: { inv } },
          );
          if (!alive) return;
          if (data?.rut) {
            setRut(formatRut(data.rut));
            reset({
              name: data.name || '',
              lastname: data.lastname || '',
              email: data.email || '',
              phone: data.phone || '',
              country: 'Chile',
              consent: false,
            });
            setStatus('ok');
          } else {
            setStatus('invalid');
          }
        } else if (ref) {
          // Afiliación validada en vivo: solo resuelve el RUT para mostrarlo.
          const { data } = await axios.get(
            `${process.env.REACT_APP_API_BACKEND}/cla/rut`,
            { params: { ref } },
          );
          if (!alive) return;
          if (data?.rut) {
            setRut(formatRut(data.rut));
            setStatus('ok');
          } else {
            setStatus('invalid');
          }
        } else {
          setStatus('invalid');
        }
      } catch {
        if (alive) setStatus('invalid');
      }
    })();
    return () => {
      alive = false;
    };
  }, [ref, inv, isInvitation, reset]);

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      // reCAPTCHA v3 (mitigación H05). Devuelve {} si no está configurado, así
      // que esto es inerte hasta que exista REACT_APP_RECAPTCHA_SITE_KEY.
      const headers = await headerRecaptcha('cla_checkout');

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BACKEND}/cla/checkout`,
        {
          name: values.name.trim(),
          lastname: values.lastname.trim(),
          email: values.email.trim(),
          country: values.country,
          phone: (values.phone || '').trim(),
          // el backend resuelve el RUT server-side desde la invitación o el ref
          ...(isInvitation ? { inv } : { ref }),
        },
        { headers },
      );
      if (data?.url) {
        window.location.href = data.url; // Webpay (Transbank)
        return;
      }
      throw new Error('no url');
    } catch (e: unknown) {
      const status = axios.isAxiosError(e) ? e.response?.status : undefined;
      // 409 = el RUT ya tiene una inscripción pagada; 422 con mensaje propio = el
      // correo pertenece a otra persona. En ambos casos el backend manda un texto
      // listo para mostrar, así que se respeta.
      const delBackend = axios.isAxiosError(e)
        ? (e.response?.data as { message?: string } | undefined)?.message
        : undefined;
      const msg =
        status === 409 || (status === 422 && delBackend)
          ? delBackend
          : status === 422
            ? isInvitation
              ? 'Tu enlace de pago no es válido o expiró. Escríbenos para reenviártelo.'
              : 'Tu enlace de inscripción no es válido o expiró. Vuelve a validar tu RUT en la página del programa.'
            : 'No se pudo iniciar el pago. Revisa tus datos e inténtalo de nuevo.';
      setErrorMsg(msg ?? 'No se pudo iniciar el pago. Revisa tus datos e inténtalo de nuevo.');
      setLoading(false);
    }
  };

  // Mientras se resuelve el ref / la invitación, un estado de carga breve.
  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#E8F1FF] to-[#F5F7FA] px-4">
        <div className="flex items-center gap-3 text-sm font-semibold text-[#0B1F3A]">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#1A73E8] border-t-transparent" />
          {isInvitation ? 'Cargando tu inscripción…' : 'Validando tu afiliación…'}
        </div>
      </div>
    );
  }

  // Sin ref/invitación válidos no se puede continuar.
  if (status === 'invalid') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#E8F1FF] to-[#F5F7FA] px-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-lg">
          <h1 className="text-lg font-bold text-[#0B1F3A]">
            {isInvitation ? 'Enlace de pago no válido' : 'Falta validar tu afiliación'}
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            {isInvitation
              ? 'Tu enlace de pago no es válido o expiró. Escríbenos para reenviártelo.'
              : 'Para inscribirte, primero valida tu RUT en la página del programa de Caja Los Andes.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#E8F1FF] to-[#F5F7FA] px-4 py-10">
      <div className="grid w-full max-w-4xl overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl lg:grid-cols-[1fr_1.1fr]">
        {/* Aside de marca */}
        <div className="relative overflow-hidden bg-[#0B1F3A] p-8 text-white lg:p-10">
          <div
            className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#1A73E8]/25 blur-3xl"
            aria-hidden
          />
          <div className="relative">
            <div className="mb-7 flex items-center gap-3">
              <img
                src={`${process.env.PUBLIC_URL}/logos/logo-adl-blanco.png`}
                alt="Desafío Latam"
                className="h-6 w-auto"
              />
              <span className="h-5 w-px bg-white/25" />
              <img
                src={`${process.env.PUBLIC_URL}/logos/caja-los-andes-wh.svg`}
                alt="Caja Los Andes"
                className="h-8 w-auto"
              />
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC857]">
              Inscripción · Caja Los Andes
            </p>
            <h1 className="mt-3 text-2xl font-extrabold leading-tight">{PROGRAMA}</h1>
            <p className="mt-2 text-sm text-white/70">
              Beneficio exclusivo para afiliados y cargas autorizadas.
            </p>

            <div className="mt-7 rounded-2xl bg-white/[0.07] p-5 ring-1 ring-white/10">
              <p className="text-xs uppercase tracking-wide text-white/50">Copago (pago único)</p>
              <div className="mt-1 flex items-end gap-2">
                <span className="text-3xl font-extrabold text-white">{COPAGO}</span>
                <span className="mb-1 text-sm text-white/50 line-through">{REFERENCIA}</span>
              </div>
              <p className="mt-1 text-xs text-[#00D69A]">Beneficio Caja Los Andes aplicado</p>
            </div>

            <div className="mt-6 flex items-center gap-2 rounded-xl bg-[#00A676]/15 px-4 py-3 ring-1 ring-[#00A676]/30">
              <TiTick className="h-5 w-5 shrink-0 text-[#00D69A]" />
              <span className="text-sm font-semibold text-white">Beneficio exclusivo afiliados Caja Los Andes</span>
            </div>

            <ul className="mt-7 space-y-2.5 text-sm text-white/80">
              <li className="flex items-start gap-2">
                <TiTick className="mt-0.5 h-4 w-4 shrink-0 text-[#00D69A]" /> 8 meses de formación en IA aplicada
              </li>
              <li className="flex items-start gap-2">
                <TiTick className="mt-0.5 h-4 w-4 shrink-0 text-[#00D69A]" /> Certificación Desafío Latam
              </li>
              <li className="flex items-start gap-2">
                <TiTick className="mt-0.5 h-4 w-4 shrink-0 text-[#00D69A]" /> Pago seguro con Webpay
              </li>
            </ul>
          </div>
        </div>

        {/* Formulario */}
        <div className="p-8 lg:p-10">
          <h2 className="text-lg font-bold text-[#0B1F3A]">Completa tu inscripción</h2>
          <p className="mt-1 text-sm text-gray-500">Solo faltan tus datos para finalizar.</p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
            {/* RUT afiliado: viene validado del landing, read-only (campo deshabilitado). */}
            <div>
              <label className={label}>RUT afiliado</label>
              <div className="relative">
                <input
                  type="text"
                  value={rut}
                  readOnly
                  disabled
                  tabIndex={-1}
                  aria-label="RUT afiliado validado"
                  className="w-full cursor-not-allowed rounded-lg border border-gray-300 bg-gray-100 px-3 py-2.5 pr-10 text-sm text-gray-600 outline-none"
                />
                <FaCheckCircle
                  className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#16A34A]"
                  aria-hidden
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={label}>Nombre *</label>
                <input className={field} {...register('name', { required: true })} />
                {errors.name && <p className="mt-1 text-xs text-red-600">Requerido</p>}
              </div>
              <div>
                <label className={label}>Apellidos *</label>
                <input className={field} {...register('lastname', { required: true })} />
                {errors.lastname && <p className="mt-1 text-xs text-red-600">Requerido</p>}
              </div>
            </div>

            <div>
              <label className={label}>Correo electrónico *</label>
              <input
                type="email"
                readOnly={isInvitation}
                className={isInvitation ? `${field} cursor-not-allowed bg-gray-100 text-gray-600` : field}
                {...register('email', { required: true })}
              />
              {errors.email && <p className="mt-1 text-xs text-red-600">Requerido</p>}
              {isInvitation && (
                <p className="mt-1 text-xs text-gray-400">Correo de tu inscripción (no editable).</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={label}>País *</label>
                <select className={field} {...register('country', { required: true })}>
                  <option value="Chile">Chile</option>
                  <option value="Argentina">Argentina</option>
                  <option value="Perú">Perú</option>
                  <option value="Colombia">Colombia</option>
                  <option value="México">México</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
              <div>
                <label className={label}>Teléfono</label>
                <input type="tel" placeholder="+56 9 ..." className={field} {...register('phone')} />
              </div>
            </div>

            <label className="flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2.5 text-xs leading-relaxed text-gray-500">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#1A73E8]"
                {...register('consent', { required: true })}
              />
              <span>
                Autorizo el tratamiento de mis datos personales para gestionar mi matrícula
                y pago en este programa, conforme a la Ley 19.628. *
              </span>
            </label>
            {errors.consent && <p className="text-xs text-red-600">Debes aceptar para continuar.</p>}

            {errorMsg && (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
                {errorMsg}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-[#1A73E8] px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#1A73E8]/25 transition hover:bg-[#155ec2] disabled:opacity-60"
            >
              {loading ? 'Redirigiendo al pago…' : `Pagar ${COPAGO} con Webpay`}
            </button>
            <p className="text-center text-xs text-gray-400">Pago seguro con Webpay (Transbank).</p>
          </form>
        </div>
      </div>
    </div>
  );
};
